import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { useWardwiseTATStats } from "@/hooks/useWardwiseTATStats";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const formatTime = (minutes: number): string => {
  if (minutes === 0) return "0:00:00";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.round((minutes - Math.floor(minutes)) * 60);
  return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

interface TATRowProps {
  ward: string;
  avgMinutes: number;
  withinTAT: number;
  slightlyOver: number;
  breached: number;
  totalCompleted: number;
}

const TATRow = ({ ward, avgMinutes, withinTAT, slightlyOver, breached, totalCompleted }: TATRowProps) => {
  const hasData = totalCompleted > 0;
  
  return (
    <div className="py-3 border-b border-border last:border-b-0">
      <div className="text-sm text-muted-foreground mb-1">{ward}</div>
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {hasData ? formatTime(avgMinutes) : "--:--:--"}
          </span>
          <span className="text-sm text-muted-foreground">Avg Discharge</span>
        </div>
        
        {hasData ? (
          <div className="flex gap-2">
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              {withinTAT}%
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {slightlyOver}%
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {breached}%
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">No data</span>
        )}
      </div>
    </div>
  );
};

export const WardwiseTATPerformance = () => {
  const { data: wardStats, isLoading } = useWardwiseTATStats();

  return (
    <Card className="w-full h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          Ward wise 24 Hours TAT Performance
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Average discharge turnaround time per ward in the last 24 hours (from MD to PD).</p>
                <div className="flex gap-2 mt-2 text-xs">
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Within TAT</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Slightly Over</span>
                  <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Breached</span>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="py-3 border-b border-border last:border-b-0">
                <Skeleton className="h-4 w-20 mb-2" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-8 w-28" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-12" />
                    <Skeleton className="h-5 w-12" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : wardStats && wardStats.length > 0 ? (
          <div>
            {wardStats.map((stat) => (
              <TATRow
                key={stat.ward}
                ward={stat.ward}
                avgMinutes={stat.avgMinutes}
                withinTAT={stat.withinTAT}
                slightlyOver={stat.slightlyOver}
                breached={stat.breached}
                totalCompleted={stat.totalCompleted}
              />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No discharge data available for the last 24 hours
          </div>
        )}
      </CardContent>
    </Card>
  );
};
